import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Select, Spin, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { ClockCircleOutlined, CalendarOutlined, WarningOutlined } from "@ant-design/icons";
import GetAttentionRequiredApi from "../../api/plans/GetAttentionRequiredApi";
import Theme from "../../theme/Theme";

const { Option } = Select;

function ProgressBar({ value }) {
  const pct = Math.min(Math.max(value || 0, 0), 100);

  // Color shifts from red → orange → green based on progress
  let barColor;
  if (pct < 30) barColor = "#ef4444";
  else if (pct < 60) barColor = "#f97316";
  else if (pct < 80) barColor = "#eab308";
  else barColor = "#22c55e";

  return (
    <div style={{ width: "100%", marginTop: "10px" }}>
      <div className="flex justify-between items-center mb-1">
        <span style={{ fontSize: "11px", color: "#888", fontFamily: "Inter, sans-serif" }}>
          Progress
        </span>
        <span style={{ fontSize: "12px", fontWeight: 600, color: barColor, fontFamily: "Inter, sans-serif" }}>
          {pct}%
        </span>
      </div>
      <div
        style={{
          width: "100%",
          height: "8px",
          backgroundColor: "#f0f0f0",
          borderRadius: "999px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            backgroundColor: barColor,
            borderRadius: "999px",
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}

function NeedsAttentionToday() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("plans");

  const positionState = useSelector((state) => state.positionState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);

  useEffect(() => {
    if (positionState?.id && perfCycleState?.id) {
      loadItems();
    }
  }, [positionState?.id, perfCycleState?.id, type]);

  async function loadItems() {
    setLoading(true);
    try {
      const res = await GetAttentionRequiredApi(
        positionState.id,
        perfCycleState.id,
        type,
        5
      );
      if (res.success) {
        setItems(res.data || []);
      }
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function urgencyColor(item) {
    if (item.isOverdue) return "#ef4444";
    if (item.daysUntilDue !== null && item.daysUntilDue <= 7) return "#f97316";
    return "#eab308";
  }

  const emptyMessages = {
    plans: "No plans need attention right now.",
    tasks: "This performance cycle doesn't have tasks configured.",
    kpis: "This performance cycle doesn't have KPIs configured.",
  };

  return (
    <div
      className="mb-6 p-6 rounded-lg"
      style={{ backgroundColor: "white" }}
    >
      <div className="flex justify-between items-center mb-5">
        <div
          className="text-lg font-semibold flex items-center gap-2"
          style={{ color: Theme().textPrimary, fontFamily: "Inter, sans-serif" }}
        >
          <WarningOutlined style={{ color: "#f97316" }} />
          Needs Your Attention Today
        </div>
        <Select
          value={type}
          onChange={setType}
          style={{ width: 130 }}
          size="small"
        >
          <Option value="plans">Plans</Option>
          <Option value="tasks">Tasks</Option>
          <Option value="kpis">KPIs</Option>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <div
          className="flex items-center justify-center py-8"
          style={{
            color: "#aaa",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
          }}
        >
          {emptyMessages[type]}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/top-plans/${item.id}`}
              style={{ textDecoration: "none", display: "block" }}
            >
              <div
                className="hover:shadow-md transition-shadow"
                style={{
                  padding: "16px 20px",
                  borderRadius: "10px",
                  border: `1px solid ${Theme().navBorder}`,
                  borderLeft: `4px solid ${urgencyColor(item)}`,
                  backgroundColor: "#fdf8f6",
                  cursor: "pointer",
                }}
              >
                {/* Top row: title + badges + deadline/staleness */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: Theme().textPrimary,
                        fontFamily: "Inter, sans-serif",
                        marginBottom: "6px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.title}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.planLevel && (
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            backgroundColor: "rgba(241, 90, 36, 0.1)",
                            color: Theme().primary,
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          {item.planLevel}
                        </span>
                      )}
                      {!item.isDirectlyOwned && (
                        <Tooltip title="You own a parent plan of this item">
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor: "rgba(99, 102, 241, 0.1)",
                              color: "#6366f1",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            via parent
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {/* Right: staleness + deadline + owner */}
                  <div
                    className="flex items-center gap-4 flex-shrink-0"
                    style={{ fontSize: "12px", color: "#888", fontFamily: "Inter, sans-serif" }}
                  >
                    <Tooltip title="Days since last update">
                      <span className="flex items-center gap-1">
                        <ClockCircleOutlined />
                        {item.daysSinceUpdate}d stale
                      </span>
                    </Tooltip>
                    <Tooltip title={item.isOverdue ? "Overdue" : "Days until due"}>
                      <span
                        className="flex items-center gap-1 font-semibold"
                        style={{ color: urgencyColor(item) }}
                      >
                        <CalendarOutlined />
                        {item.isOverdue
                          ? `${Math.abs(item.daysUntilDue)}d overdue`
                          : `${item.daysUntilDue}d left`}
                      </span>
                    </Tooltip>
                    {item.owner && (item.owner.firstName || item.owner.lastName) && (
                      <Tooltip title={item.owner.designationName || "Assigned to"}>
                        <div className="flex items-center gap-1.5">
                          {item.owner.imageUrl ? (
                            <img
                              src={item.owner.imageUrl}
                              alt=""
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                backgroundColor: Theme().primary,
                                color: "white",
                                fontSize: "10px",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {(item.owner.firstName?.[0] || "") + (item.owner.lastName?.[0] || "")}
                            </div>
                          )}
                          <span style={{ color: Theme().textPrimary, fontWeight: 500 }}>
                            {item.owner.firstName} {item.owner.lastName}
                          </span>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Progress bar — full width, fluid */}
                <ProgressBar value={item.performance} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
export default NeedsAttentionToday;
